import { useMemo, useState } from 'react'
import type {
    BossLegacyImportResult, BossLegacyStoreGroup, BossReportSummary, BossShortcutGroup,
} from '../../types/boss'
import { BossBadges } from './ShortcutPanel'
// The picker's filter box behaves exactly like the pre-filter dialog's — one implementation,
// two callers, so a quoted phrase means the same thing everywhere in this feature.
import { matchesFilterQuery } from './PreFilterDialog'

export type ReportPickerTab = 'web' | 'legacy'

export type ReportPickerProps = {
    /** Which tab is showing. Held by the page because the legacy query is enabled off it. */
    tab?: ReportPickerTab
    onTab?: (tab: ReportPickerTab) => void
    /** boss.reports.import. Cosmetic only — /legacy/* is gated on the same key on the server. */
    canImport?: boolean
    /**
     * boss.reports.delete. Cosmetic only — DELETE /reports/{id} is gated on the same key on the
     * server, so hiding the button never decides anything.
     */
    canDelete?: boolean

    reports?: BossReportSummary[]
    reportsLoading?: boolean
    reportsError?: string | null

    legacyGroups?: BossLegacyStoreGroup[]
    legacyLoading?: boolean
    legacyError?: string | null

    importing?: boolean
    importResult?: BossLegacyImportResult | null

    /** Groups a report can be pinned into. Empty means "make a group first". */
    shortcutGroups?: BossShortcutGroup[]
    selectedReportId?: string

    onOpenReport?: (reportId: string) => void
    onPin?: (groupId: string, reportId: string) => void
    onImport?: (legacyReportGuids: string[], onConflict: 'skip' | 'overwrite') => void
    /** Asks for the confirm dialog; the page owns it, because deleting outlives this modal. */
    onDeleteReport?: (reportId: string, name: string) => void
    onClose?: () => void
}

/** Runs the legacy filter-box rule over everything about a row a user might type. */
function matches(query: string, ...haystack: (string | null | undefined)[]): boolean {
    return matchesFilterQuery(haystack.filter(Boolean).join(' '), query)
}

/**
 * The "open a report" modal: web reports grouped by the store they came from, and — for someone
 * who may import — the legacy BOSS corpus grouped the same way, with an already-imported marker.
 *
 * Every prop is optional so the modal can be rendered in isolation by the style/modal tests.
 */
export default function ReportPicker({
    tab = 'web', onTab, canImport = false, canDelete = false,
    reports = [], reportsLoading = false, reportsError = null,
    legacyGroups = [], legacyLoading = false, legacyError = null,
    importing = false, importResult = null,
    shortcutGroups = [], selectedReportId = '',
    onOpenReport, onPin, onImport, onDeleteReport, onClose,
}: ReportPickerProps) {
    const [query, setQuery] = useState('')
    const [picked, setPicked] = useState<string[]>([])
    const [replaceExisting, setReplaceExisting] = useState(false)

    // The legacy corpus is only offered to someone who may import it: the endpoint requires
    // boss.reports.import, so showing the tab to anyone else only buys a 403.
    const showLegacy = canImport
    const activeTab: ReportPickerTab = showLegacy ? tab : 'web'

    /**
     * The Web tab, grouped by the STORE a report came from — not by whoever imported it.
     *
     * Report definitions are shared: every user sees every report, and a legacy import stamps
     * `ownerDisplayName` with whichever administrator happened to click Import. Grouping by that
     * name filed the whole corpus under one person and read as "these are someone else's
     * reports", which is the opposite of what they are. The store of origin is the grouping the
     * legacy tool itself used and the one that means something to a reader.
     *
     * `ownerDisplayName` stays searchable — "who brought this in" is still a fair thing to type.
     */
    const byOrigin = useMemo(() => {
        const map = new Map<string, BossReportSummary[]>()
        for (const r of reports) {
            // Deliberately not searched: memo. In this corpus the legacy Memo field was used as a
            // scratchpad for the report's own SQL, so matching it would surface rows for reasons
            // the user cannot see — and the picker no longer shows the memo at all.
            if (!matches(query, r.name, r.ownerDisplayName, r.legacyStoreName)) continue
            const origin = r.legacyStoreName ?? 'Built on the web'
            const bucket = map.get(origin)
            if (bucket) bucket.push(r)
            else map.set(origin, [r])
        }
        return [...map.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([origin, rows]) => ({
                origin,
                rows: rows.slice().sort((a, b) => a.name.localeCompare(b.name)),
            }))
    }, [reports, query])

    const legacyFiltered = useMemo(() => legacyGroups
        .map(g => ({
            ...g,
            reports: g.reports.filter(r => matches(query, r.name, r.linkedReportName, g.storeLabel)),
        }))
        .filter(g => g.reports.length > 0), [legacyGroups, query])

    const toggle = (guid: string) =>
        setPicked(cur => (cur.includes(guid) ? cur.filter(g => g !== guid) : [...cur, guid]))

    const toggleGroup = (guids: string[]) =>
        setPicked(cur => (guids.every(g => cur.includes(g))
            ? cur.filter(g => !guids.includes(g))
            : [...cur, ...guids.filter(g => !cur.includes(g))]))

    const startImport = () => {
        if (picked.length === 0) return
        onImport?.(picked, replaceExisting ? 'overwrite' : 'skip')
        setPicked([])
    }

    const needsReview = importResult?.results.filter(r => r.needsReview) ?? []

    // Dimming modal: deliberately no backdrop onClick — dialogs must not close on outside-click.
    return (
        <div className="modal-backdrop">
            <div className="modal boss-picker">
                <div className="modal-header">
                    <h2>Open a report</h2>
                    <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="modal-body">
                    {showLegacy && (
                        <div className="boss-tabs" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'web'}
                                className={`boss-tab${activeTab === 'web' ? ' active' : ''}`}
                                onClick={() => onTab?.('web')}
                            >Web reports</button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'legacy'}
                                className={`boss-tab${activeTab === 'legacy' ? ' active' : ''}`}
                                onClick={() => onTab?.('legacy')}
                            >Import from Legacy</button>
                        </div>
                    )}

                    <input
                        className="form-input boss-filter-box"
                        style={{ marginTop: 'var(--space-3)' }}
                        placeholder={'Filter reports — words in any order, or "an exact phrase"'}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                    />

                    {activeTab === 'web' ? (
                        <div className="boss-picker-list">
                            {reportsLoading && <div className="boss-muted">Loading reports…</div>}
                            {reportsError && <div className="boss-error">{reportsError}</div>}
                            {!reportsLoading && !reportsError && byOrigin.length === 0 && (
                                <div className="boss-empty">No reports match that filter.</div>
                            )}
                            {byOrigin.map(group => (
                                <div key={group.origin} className="boss-picker-group">
                                    <div className="boss-picker-group-title">{group.origin}</div>
                                    {group.rows.map(r => (
                                        <div
                                            key={r.id}
                                            className={`boss-picker-row${selectedReportId === r.id ? ' active' : ''}`}
                                        >
                                            <div className="boss-picker-main">
                                                <div className="boss-picker-name">
                                                    <span>{r.name}</span>
                                                    <BossBadges
                                                        isCore={r.isCore}
                                                        hasPreFilters={r.hasPreFilters}
                                                        hasMyStore={r.hasMyStore}
                                                        needsReview={r.needsReview}
                                                    />
                                                </div>
                                                {(r.linkedReportName || r.legacyStoreName) && (
                                                    <div className="boss-picker-memo">
                                                        {[
                                                            r.linkedReportName ? `inherits its query from “${r.linkedReportName}”` : null,
                                                            r.legacyStoreName ? `imported from ${r.legacyStoreName}` : null,
                                                        ].filter(Boolean).join(' · ')}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="boss-picker-actions">
                                                {shortcutGroups.length > 0 && (
                                                    <select
                                                        className="form-select boss-pin-select"
                                                        value=""
                                                        aria-label={`Pin ${r.name} to a shortcut group`}
                                                        onChange={e => {
                                                            if (e.target.value) onPin?.(e.target.value, r.id)
                                                        }}
                                                    >
                                                        <option value="">Pin to…</option>
                                                        {shortcutGroups.map(g => (
                                                            <option key={g.id} value={g.id}>{g.name}</option>
                                                        ))}
                                                    </select>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger boss-btn-sm"
                                                        title={`Permanently delete ${r.name}`}
                                                        onClick={() => onDeleteReport?.(r.id, r.name)}
                                                    >Delete</button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="btn btn-primary boss-btn-sm"
                                                    onClick={() => onOpenReport?.(r.id)}
                                                >Open</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="boss-picker-list">
                            {legacyLoading && <div className="boss-muted">Reading the legacy report list…</div>}
                            {legacyError && <div className="boss-error">{legacyError}</div>}
                            {!legacyLoading && !legacyError && legacyFiltered.length === 0 && (
                                <div className="boss-empty">No legacy reports match that filter.</div>
                            )}
                            {legacyFiltered.map(g => {
                                const guids = g.reports.map(r => r.legacyReportGuid)
                                const allPicked = guids.every(id => picked.includes(id))
                                return (
                                    <div key={g.storeLabel} className="boss-picker-group">
                                        <div className="boss-picker-group-title">
                                            <span>{g.storeLabel}</span>
                                            <button
                                                type="button"
                                                className="boss-icon-btn"
                                                onClick={() => toggleGroup(guids)}
                                            >{allPicked ? 'clear' : 'select all'}</button>
                                        </div>
                                        {g.reports.map(r => (
                                            <div key={r.legacyReportGuid} className="boss-picker-row">
                                                <input
                                                    type="checkbox"
                                                    className="boss-picker-check"
                                                    aria-label={`Select ${r.name}`}
                                                    checked={picked.includes(r.legacyReportGuid)}
                                                    onChange={() => toggle(r.legacyReportGuid)}
                                                />
                                                <div className="boss-picker-main">
                                                    <div className="boss-picker-name">
                                                        <span>{r.name}</span>
                                                        <BossBadges isCore={r.isCore} />
                                                        {r.alreadyImported && (
                                                            <span
                                                                className="boss-badge boss-badge-imported"
                                                                title="This report has already been imported"
                                                            >✓ imported</span>
                                                        )}
                                                    </div>
                                                    {!r.hasOwnSql && (
                                                        <div className="boss-picker-memo">
                                                            {`inherits its query from “${r.linkedReportName ?? 'a linked report'}”`}
                                                        </div>
                                                    )}
                                                </div>
                                                {r.importedReportId !== null && (
                                                    <div className="boss-picker-actions">
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary boss-btn-sm"
                                                            onClick={() => onOpenReport?.(String(r.importedReportId))}
                                                        >Open</button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )
                            })}

                            {importResult && (
                                <div className="boss-import-result">
                                    <div>
                                        Imported {importResult.imported}, skipped {importResult.skipped},
                                        failed {importResult.failed}.
                                    </div>
                                    {needsReview.length > 0 && (
                                        <div className="boss-import-review">
                                            These imported but cannot run until a designer fixes them:
                                            <ul>
                                                {needsReview.map(r => (
                                                    <li key={r.legacyReportGuid}>
                                                        <strong>{r.name}</strong> — {r.needsReviewReason}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <div className="boss-picker-foot">
                        {activeTab === 'legacy' && (
                            <>
                                <label className="boss-check">
                                    <input
                                        type="checkbox"
                                        checked={replaceExisting}
                                        onChange={e => setReplaceExisting(e.target.checked)}
                                    />
                                    Replace definitions that were already imported
                                </label>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={importing || picked.length === 0}
                                    onClick={startImport}
                                >{importing ? 'Importing…' : `Import ${picked.length} selected`}</button>
                            </>
                        )}
                        <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ marginLeft: 'auto' }}
                            onClick={onClose}
                        >Close</button>
                    </div>
                </div>
            </div>
        </div>
    )
}
